import { angleBetween, classMean, type SignaturePoint, type TrainingClass } from './sam';

export interface PerClass {
  name: string;
  /** Training points held out for this class. */
  n: number;
  /** Of this class's held-out points, the share the classifier got right. */
  producer: number | null;
  /** Of everything assigned to this class, the share that really belongs. */
  user: number | null;
}

export interface Assessment {
  tested: number;
  correct: number;
  overall: number;
  unclassified: number;
  /** matrix[actual][predicted], with a final column for "matched nothing". */
  matrix: number[][];
  perClass: PerClass[];
  /** Classes that contributed, in matrix order. */
  names: string[];
}

/** Nearest class by spectral angle, or -1 if nothing falls inside the threshold. */
export function classifyPoint(
  pts: SignaturePoint[], means: SignaturePoint[][], maxAngleDeg: number,
): number {
  let best = -1;
  let bestAngle = Infinity;
  means.forEach((m, i) => {
    const a = angleBetween(pts, m);
    if (a != null && a < bestAngle) { bestAngle = a; best = i; }
  });
  return bestAngle <= maxAngleDeg ? best : -1;
}

/**
 * Hold-out accuracy: train on most of the points, classify the rest, compare.
 *
 * Scoring a classifier on the points it was trained from measures memorisation, not
 * skill — it would report near-perfect accuracy however badly the classes were drawn.
 * Every `holdout`-th point is withheld instead, which is deterministic, so the number
 * does not jitter as the panel re-renders.
 *
 * Producer accuracy answers "was this class found"; user accuracy answers "is what was
 * found really this class". They differ, and the gap is usually where the problem is:
 * a class with high producer and low user accuracy is swallowing its neighbours.
 */
export function assess(
  classes: TrainingClass[], maxAngleDeg: number, holdout = 3,
): Assessment | null {
  const usable = classes.filter((c) => c.points.length >= holdout);
  if (usable.length < 2) return null;

  const train: SignaturePoint[][][] = [];
  const test: SignaturePoint[][][] = [];
  for (const c of usable) {
    const tr: SignaturePoint[][] = [];
    const te: SignaturePoint[][] = [];
    c.points.forEach((p, i) => ((i % holdout === holdout - 1) ? te : tr).push(p));
    train.push(tr);
    test.push(te);
  }
  if (train.some((t) => t.length === 0) || test.every((t) => t.length === 0)) return null;

  const means = train.map((t) => classMean(t));
  if (means.some((m) => m == null)) return null;

  const k = usable.length;
  const matrix = Array.from({ length: k }, () => new Array<number>(k + 1).fill(0));
  let tested = 0, correct = 0, unclassified = 0;

  test.forEach((pts, actual) => {
    for (const p of pts) {
      const pred = classifyPoint(p, means as SignaturePoint[][], maxAngleDeg);
      tested++;
      if (pred < 0) { matrix[actual]![k]!++; unclassified++; continue; }
      matrix[actual]![pred]!++;
      if (pred === actual) correct++;
    }
  });
  if (!tested) return null;

  const perClass: PerClass[] = usable.map((c, i) => {
    const actualTotal = matrix[i]!.reduce((a, b) => a + b, 0);
    const predTotal = matrix.reduce((a, row) => a + row[i]!, 0);
    return {
      name: c.name,
      n: actualTotal,
      producer: actualTotal ? matrix[i]![i]! / actualTotal : null,
      user: predTotal ? matrix[i]![i]! / predTotal : null,
    };
  });

  return {
    tested, correct, overall: correct / tested, unclassified,
    matrix, perClass, names: usable.map((c) => c.name),
  };
}
